import { useMutation, useQueryClient } from '@tanstack/react-query';
import { storageSafeFileName } from '@releve/shared';
// eslint-disable-next-line no-restricted-imports -- storage upload is the sanctioned browser-side call (docs/BACKEND_API.md "What lives where"); the row is written by POST /files
import { supabase } from '@/integrations/supabase/client';
import { authedPost } from '@/lib/api';
import { captureException } from '@/lib/sentry';

/** One file for the Vault, with the row fields the upload sets. */
export interface VaultFileUpload {
  file: File;
  /** Stored name; defaults to the file's own name. */
  name?: string;
  file_type: string;
  project_id?: string | null;
  tags?: string[];
}

/**
 * Upload one file to the Vault: bytes to storage, then the `files` row
 * through Hono (Pattern B). The route's schema is `.strict()` —
 * organization_id (from the URL), created_by (from the token), code (the
 * `auto_generate_file_code` trigger) and metadata are server-pinned, so none
 * of them ride along. `tags` goes top-level; the route folds it into
 * `metadata.tags` (`files` has no tags column).
 *
 * If the row insert fails, the storage object is removed (best effort — it
 * never replaces the original error), so no object lingers unowned.
 */
export async function uploadVaultFile(
  organizationId: string,
  { file, name, file_type, project_id, tags }: VaultFileUpload,
): Promise<void> {
  // Key-safe, unique object name; the row's `name` keeps the original.
  const storagePath = `${organizationId}/${crypto.randomUUID()}-${storageSafeFileName(file.name)}`;

  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('files')
    .upload(storagePath, file);
  if (uploadError) throw uploadError;

  try {
    await authedPost(`/api/v1/files/orgs/${organizationId}/files`, {
      name: name ?? file.name,
      file_type,
      file_path: uploadData.path,
      file_size: file.size,
      content_type: file.type,
      project_id: project_id || null,
      tags: tags ?? [],
    });
  } catch (insertError) {
    try {
      await supabase.storage.from('files').remove([storagePath]);
    } catch {
      // Compensation is advisory; the insert error is the one to surface.
    }
    throw insertError;
  }
}

export interface UploadVaultFilesVariables {
  organizationId: string;
  uploads: VaultFileUpload[];
}

export interface UploadVaultFilesResult {
  uploaded: number;
  /** The files that failed (the rest were uploaded). */
  failed: File[];
}

/**
 * Upload several files to the Vault, one by one; a failure does not stop the
 * rest. Rejects only when NOTHING was uploaded (with the first failure).
 *
 * Silent like the other Vault write hooks (`useVaultMutations.ts`): the
 * caller owns `t` and raises the toasts. Failures are reported to Sentry
 * here. Invalidates `['files', organizationId]`, the prefix of the Vault
 * index key.
 */
export function useUploadVaultFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, uploads }: UploadVaultFilesVariables): Promise<UploadVaultFilesResult> => {
      let uploaded = 0;
      const failed: File[] = [];
      let firstError: unknown = null;

      for (const upload of uploads) {
        try {
          await uploadVaultFile(organizationId, upload);
          uploaded += 1;
        } catch (error) {
          failed.push(upload.file);
          firstError ??= error;
          captureException(error instanceof Error ? error : new Error(String(error)), {
            context: 'Vault file upload',
            fileCount: uploads.length,
          });
        }
      }

      if (uploaded === 0 && firstError) throw firstError;
      return { uploaded, failed };
    },
    onSettled: (_result, _error, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
    },
  });
}
